import React, { useCallback, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import "./App.css";
import Dashboard from "./pages/dashboard";
import Store from "./pages/stores";
import Gdpr from "./pages/gdpr";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Frame, Navigation, TopBar, useBreakpoints } from "@shopify/polaris";
import "./pages/global.css";
import ProtectedRoute from "./context/auth/protect-route";
import NotFound from "./common/NotFound";
import {
  HistoricalOrdersLayout,
  DisputesLayout,
  OrdersLayout,
  StoreEmLayoutShell,
} from "./pages/userCustomDashboard/emLayout";
import StoreSupportOverview from "./pages/storeSupport/Overview";
import StoreSyncActivity from "./pages/storeSupport/SyncActivity";
import StoreConfiguration from "./pages/storeSupport/Configuration";
import StoreEventsAndLogs from "./pages/storeSupport/EventsAndLogs";
import StoreActions from "./pages/storeSupport/Actions";

import {
  CircleRightMajor,
  HomeMajor,
  StoreMajor,
  AnalyticsMajor
} from "@shopify/polaris-icons";
import { HeaderLogo } from "./assets";

const Main = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mdDown } = useBreakpoints();
  const skipToContentRef = useRef(null);
  const [userMenuActive, setUserMenuActive] = useState(false);
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);

  const { user = {}, logout } = useAuth0();
  const name = user?.nickname;

  const toggleUserMenuActive = useCallback(() => {
    setUserMenuActive((previous) => !previous);
  }, []);

  const toggleMobileNavigationActive = useCallback(() => {
    setMobileNavigationActive((previous) => !previous);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    sessionStorage.clear();
    logout({
      logoutParams: { returnTo: window.location.origin },
    });
  }, [logout]);

  const handleNavigation = (path) => {
    navigate(path);
    if (mdDown) {
      setMobileNavigationActive(false);
    }
  };

  const currentPath = location.pathname;
  const currentStoreIdFromPath = useMemo(() => {
    const match = currentPath.match(/^\/store\/([^/]+)/);
    return match ? match[1] : null;
  }, [currentPath]);
  const selectedStoreId = currentStoreIdFromPath;
  const isStoreRoute = Boolean(currentStoreIdFromPath);
  const isAuthRoute = ["/", "/login", "/logout", "/oauth2/redirect"].includes(
    currentPath
  );

  if (isAuthRoute) {
    return null;
  }

  const userMenuActions = [
    {
      items: [
        {
          content: "Logout",
          icon: CircleRightMajor,
          onAction: handleLogout,
        },
      ],
    },
  ];

  const userMenuMarkup = (
    <TopBar.UserMenu
      actions={userMenuActions}
      name={name || "User"}
      initials={(name || "S").toString().trim().charAt(0).toUpperCase() || "S"}
      open={userMenuActive}
      onToggle={toggleUserMenuActive}
    />
  );

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      userMenu={userMenuMarkup}
      onNavigationToggle={toggleMobileNavigationActive}
    />
  );

  const appNavigationMarkup = (
    <Navigation location={currentPath}>
      <Navigation.Section
        items={[
          {
            label: "Operations",
            icon: HomeMajor,
            selected: currentPath === "/home" || currentPath === "/dashboard",
            onClick: () => handleNavigation("/home"),
          },
          {
            label: "Stores",
            icon: StoreMajor,
            selected: currentPath === "/stores",
            onClick: () => handleNavigation("/stores"),
          },
          {
            label: "Privacy / GDPR",
            icon: AnalyticsMajor,
            selected: currentPath === "/gdpr",
            onClick: () => handleNavigation("/gdpr"),
          },
        ]}
      />
      {isStoreRoute && selectedStoreId ? (
        <Navigation.Section
          title={`Store ${selectedStoreId}`}
          items={[
            {
              label: "Back to stores",
              selected: false,
              onClick: () => handleNavigation("/stores"),
            },
            {
              label: "Overview",
              selected: currentPath === `/store/${selectedStoreId}`,
              onClick: () => handleNavigation(`/store/${selectedStoreId}`),
            },
            {
              label: "Sync activity",
              selected: currentPath === `/store/${selectedStoreId}/sync`,
              onClick: () => handleNavigation(`/store/${selectedStoreId}/sync`),
            },
            {
              label: "Orders",
              selected: currentPath === `/store/${selectedStoreId}/orders`,
              onClick: () => handleNavigation(`/store/${selectedStoreId}/orders`),
            },
            {
              label: "Disputes",
              selected: currentPath === `/store/${selectedStoreId}/disputes`,
              onClick: () => handleNavigation(`/store/${selectedStoreId}/disputes`),
            },
            {
              label: "Historical imports",
              selected: currentPath === `/store/${selectedStoreId}/historical-orders`,
              onClick: () => handleNavigation(`/store/${selectedStoreId}/historical-orders`),
            },
            {
              label: "Configuration",
              selected: currentPath === `/store/${selectedStoreId}/configuration`,
              onClick: () => handleNavigation(`/store/${selectedStoreId}/configuration`),
            },
            {
              label: "Events & logs",
              selected: currentPath === `/store/${selectedStoreId}/events`,
              onClick: () => handleNavigation(`/store/${selectedStoreId}/events`),
            },
            {
              label: "Actions",
              selected: currentPath === `/store/${selectedStoreId}/actions`,
              onClick: () => handleNavigation(`/store/${selectedStoreId}/actions`),
            },
          ]}
        />
      ) : null}
    </Navigation>
  );

  const frameLogo = {
    width: 30,
    topBarSource: HeaderLogo,
    accessibilityLabel: "Palsync",
  };

  return (
    <>
      <Frame
        skipToContentTarget={skipToContentRef}
        topBar={topBarMarkup}
        navigation={appNavigationMarkup}
        logo={frameLogo}
        showMobileNavigation={mobileNavigationActive}
        onNavigationDismiss={toggleMobileNavigationActive}
      >
        <Routes>
          <Route path="/home" element={<ProtectedRoute component={Dashboard} />} />
          <Route path="/dashboard" element={<Navigate to="/home" replace />} />
          <Route path="/stores" element={<ProtectedRoute component={Store} />} />
          <Route path="/global" element={<Navigate to="/home" replace />} />
          <Route path="/carrier" element={<Navigate to="/home" replace />} />
          <Route path="/report" element={<Navigate to="/home" replace />} />
          <Route path="/gdpr" element={<ProtectedRoute component={Gdpr} />} />
          <Route path="/setting" element={<Navigate to="/home" replace />} />
          <Route path="/trobleshoot" element={<Navigate to="/home" replace />} />
          <Route
            path="/store/:id"
            element={<ProtectedRoute component={StoreEmLayoutShell} />}
          >
            <Route path="" element={<StoreSupportOverview />} />
            <Route path="sync" element={<StoreSyncActivity />} />
            <Route path="orders" element={<OrdersLayout />} />
            <Route path="disputes" element={<DisputesLayout />} />
            <Route path="historical-orders" element={<HistoricalOrdersLayout />} />
            <Route path="configuration" element={<StoreConfiguration />} />
            <Route path="events" element={<StoreEventsAndLogs />} />
            <Route path="actions" element={<StoreActions />} />
            {/* Backward-compatible aliases */}
            <Route path="trouble-shooting" element={<Navigate to="../events" replace />} />
            <Route path="dispute-management" element={<Navigate to="../disputes" replace />} />
            <Route path="plan" element={<Navigate to=".." replace />} />
            <Route path="subscription" element={<Navigate to=".." replace />} />
            <Route path="settings" element={<Navigate to="../configuration" replace />} />
            <Route path="paypalsetting" element={<Navigate to="../configuration" replace />} />
            <Route path="digital-product" element={<Navigate to="../configuration" replace />} />
            <Route path="courier-mapping" element={<Navigate to="../configuration" replace />} />
            <Route path="settings/courier-mapping" element={<Navigate to="../configuration" replace />} />
            <Route path="settings/digital-product" element={<Navigate to="../configuration" replace />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Frame>
    </>
  );
};

export default Main;
